import React, { useEffect, useState } from "react";
import Tooltip from '@mui/material/Tooltip';
import { Typography } from "@mui/material";
import i18next from "i18next";
import { darkGrey, errorColor, infoColor, orangeColor, primary } from "../../style/globalStyle";
import DocViewerCard from "../../components/doc-viewer/DocViewerCard";
import DocViewerTable from "../../components/doc-viewer/DocViewerTable";
import { SubscriptionsControls } from "../../services/common/Constants";
import useStyles from "../../style/js-style/containers/doc-viewer/PanelAdditionalChecksStyle";
import { induedScoreSeverity } from "../../services/common/InduedUtils"
import { getEvDataScopeChangeFieldValue } from "services/common/EcovadisUtils";
import { EcovadisScorePerformanceLabel } from "components/thirdparties/ecovadis/EcovadisScoresDisplay/EcovadisGlobalScore";
import EAIcon from "components/common/EAIcon";

const SisIdScoreLabel = ({ score }) => {
  let label;
  let color;
  if (score === 0) {
    label = "atRisk";
    color = errorColor;
  } else if (score <= 20) {
    label = "low";
    color = errorColor;
  } else if (score >= 90) {
    label = "important";
    color = primary;
  } else {
    label = "medium";
    color = orangeColor;
  }
  return (
    <Typography variant="body2" component="span" style={{ color, fontWeight: 600, fontSize: "12px" }}>
      {i18next.t(label)}
    </Typography>
  )
}

const TrustpairState = ({ state }) => {
  let color;
  switch (state) {
    case 'favorable':
      color = primary;
      break;
    case 'pending':
      color = infoColor;
      break;
    case 'unfavorable':
    case 'anomaly':
      state = "unfavorable";
      color = errorColor;
      break;
    case 'unevaluated':
      color = darkGrey;
      break;
    default:
      color = orangeColor;
      break;
  }
  return (
    <Typography variant="body2" component="span" sx={{ color }}>
      {i18next.t(state)}
    </Typography>
  )
}

export const InduedScoreLabel = ({ score }) => {
  const { classes } = useStyles();
  const severity = induedScoreSeverity(score);
  const label = i18next.t(`indued.severity.${severity.label}`);
  return (
    <Typography variant="body2" component="span"
      className={classes.scoreLabel + " " + classes[`scoreLabel${severity.label}`]}
    >
      {label}
    </Typography>
  )
}

const buildSisIdBody = (data) => {
  return [
    [
      i18next.t('viewer.confidenceIndex'),
      <SisIdScoreLabel score={data.score} noTooltip />,
    ],
    [
      i18next.t('viewer.reasons'),
      <>
        {mapSisIdReasons(Object.keys(data?.reasonLabels).map((key) => {
          const label = data.reasonLabels[key];
          return <Reason key={key} label={getSisIdReasonsTranlation(key, label)} />;
        }))}
      </>,
    ],
    [{ separator: true, title: '' }],
  ];
}

const buildTrustpairBody = (data) => {
  return [
    [i18next.t("viewer.evaluation"), <TrustpairState state={data.state} noTooltip />],
    [i18next.t("viewer.reasons"),
    <>
      {mapTrustpairReasons(data?.state_reasons).map((label, index) => <Reason key={index} label={getTrustpairReasonsTraduction(label)} />)}
    </>
    ],
    [{ separator: true, title: "" }]
  ];
}

const buildInduedBody = (data) => {
  return [
    [i18next.t("indued.complianceScore"), <InduedScoreLabel score={data.due_diligence.risk_level} noTooltip />],
    [{ separator: true, title: "" }]
  ];
}

const buildEcovadisBody = (data) => {
  return [
    [
      i18next.t("ecovadis.scores.global_score"),
      <EcovadisScorePerformanceLabel evData={data} score={"global_score"} className="simple" />
    ],
    [{ separator: true, title: "" }]
  ];
}

const isV2SmartCheck = (data, v2smartCheck) => {

  const receivabilityResult = data?.receivabilityResult;
  if (!receivabilityResult) return v2smartCheck;

  // V2 keys check
  const hasV2Keys =
    receivabilityResult.hasOwnProperty("errors") ||
    receivabilityResult.hasOwnProperty("right_document");
  
  return v2smartCheck && hasV2Keys;
};

const buildSmartChecksBody = (smartControlResult, checkFailed, v2smartCheck) => {
  const result = [];
  let createdAt = null;

  if (checkFailed && smartControlResult == null) {
    result.push([
      i18next.t("viewer.smartCheckDetails.resultLabel"),
      i18next.t("viewer.smartCheckDetails.checkFailedMsg")
    ]);
    return { body: result, createdAt };
  }

  try {
    const smartControlData = typeof smartControlResult === "string"
      ? JSON.parse(smartControlResult)
      : smartControlResult;

    const pushCompanyInfo = (labelKey, value, tooltipFoundKey, tooltipNotFoundKey) => {
      if (value !== undefined) {
        result.push([
          i18next.t(`viewer.smartCheckDetails.${labelKey}`),
          {
            value,
            tooltip: i18next.t(value ? `viewer.smartCheckDetails.${tooltipFoundKey}` : `viewer.smartCheckDetails.${tooltipNotFoundKey}`),
            icon: value ? 'valid-rounded' : 'warning-rounded'
          }
        ]);
      }
    };

    if (isV2SmartCheck(smartControlData, v2smartCheck)) {
      console.log('v2');
      
      if (smartControlData.receivability === true) {
        result.push([
          i18next.t("viewer.smartCheckDetails.resultLabel"),
          i18next.t("viewer.smartCheckDetails.receivabilityControlledMsg")
        ]);
      } else {
        result.push([
          i18next.t("viewer.smartCheckDetails.resultLabel"),
          smartControlData.receivabilityResult?.global_error ||
          i18next.t("viewer.smartCheckDetails.receivabilityFalseMsg")
        ]);
      }

      result.push([{ separator: true, title: i18next.t("viewer.smartCheckDetails.detailsTitle") }]);

      const badDocError = smartControlData.receivabilityResult?.errors?.bad_document_error;
      const rightDocument = smartControlData.receivabilityResult?.right_document;

      if (badDocError !== "not_checked") {
        if (rightDocument === true) {
          result.push([
            i18next.t("viewer.smartCheckDetails.receivabilityLabel"),
            i18next.t("viewer.smartCheckDetails.receivabilityTrueMsg")
          ]);
        } else {
          result.push([
            i18next.t("viewer.smartCheckDetails.receivabilityLabel"),
            badDocError || i18next.t("viewer.smartCheckDetails.defaultErrorMsg")
          ]);
        }
      }


      const companyInfo = smartControlData.receivabilityResult?.company_informations;
      const companyError = smartControlData.receivabilityResult?.errors?.company_informations_error;
      const companyFound = companyInfo?.found;
      const isCompanyFound = companyFound !== null && companyFound !== undefined;
      
      if (companyError !== "not_checked" && isCompanyFound) {
        if (companyFound === true) {
          result.push([
            i18next.t("viewer.smartCheckDetails.companyInfoLabel"),
            i18next.t("viewer.smartCheckDetails.companyInfoValidMsg")
          ]);
        } else {
          result.push([
            i18next.t("viewer.smartCheckDetails.companyInfoLabel"),
            companyError || i18next.t("viewer.smartCheckDetails.companyInfoInvalidMsg")
          ]);
        }
      
        pushCompanyInfo("identifierLabel", companyInfo.identifier?.found, "identifierTooltipFound", "identifierTooltipNotFound");
        pushCompanyInfo("sirenLabel", companyInfo.siren?.found, "sirenTooltipFound", "sirenTooltipNotFound");
        pushCompanyInfo("siretLabel", companyInfo.siret?.found, "siretTooltipFound", "siretTooltipNotFound");
        pushCompanyInfo("tvaLabel", companyInfo.tva?.found, "tvaTooltipFound", "tvaTooltipNotFound");
        pushCompanyInfo("companyNameLabel", companyInfo.company_name?.found, "companyNameTooltipFound", "companyNameTooltipNotFound");
      }

      const expiration = smartControlData.receivabilityResult?.expiration;
      const expirationError = smartControlData.receivabilityResult?.errors?.expiration_error;

      if (expirationError !== "not_checked") {
        if (expiration?.is_expired === false) {
          result.push([
            i18next.t("viewer.smartCheckDetails.dateCheckLabel"),
            i18next.t("viewer.smartCheckDetails.dateCheckValidMsg")
          ]);
        } else {
          result.push([
            i18next.t("viewer.smartCheckDetails.dateCheckLabel"),
            expirationError || i18next.t("viewer.smartCheckDetails.dateCheckInvalidMsg")
          ]);
        }
      }

      const hasExtractions = smartControlData.informations?.length > 0 || smartControlData.dates?.length > 0;
      if (hasExtractions) {
        result.push([{ separator: true, title: i18next.t("viewer.smartCheckDetails.smartControlExtractionsTitle") }]);

        smartControlData.informations?.forEach(info => {
          const displayName = info.label?.trim() ? info.label : info.name;
        
          let value;
          if (info.name === "iban") {
            // console.log(smartControlData.receivabilityResult?.iban_found)
            value = typeof info.value === "boolean"
            ? (info.value
                ? i18next.t("viewer.smartCheckDetails.booleanTrue")
                : i18next.t("viewer.smartCheckDetails.booleanFalse"))
            : 
              <>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {smartControlData.receivabilityResult?.iban_found === false ? (
                    <Tooltip
                      title={`${i18next.t("viewer.smartCheckDetails.ibanMismatch")}  
                        ${smartControlData.receivabilityResult?.iban_filled_by_tp}`}
                      placement="top"
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          cursor: "help",
                        }}
                      >
                        {info.value}
                        <EAIcon style={{ width: 22, height: 22 }} icon="warning-rounded" />
                      </span>
                    </Tooltip>
                  ) : (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      {info.value}
                    </span>
                  )}
                </span>
              </>
            || i18next.t("viewer.smartCheckDetails.noValueFound");
            result.push([displayName, value]);

            // result.push([displayName, value]);

          } else {
            value = typeof info.value === "boolean"
              ? (info.value
                  ? i18next.t("viewer.smartCheckDetails.booleanTrue")
                  : i18next.t("viewer.smartCheckDetails.booleanFalse"))
              : info.value || i18next.t("viewer.smartCheckDetails.noValueFound");
              result.push([displayName, value]);
          }
        });
        

        smartControlData.dates?.forEach(date => {
          const displayName = date.label?.trim() ? date.label : date.name;
          result.push([displayName, date.formatted || i18next.t("viewer.smartCheckDetails.noValueFound")]);
        });
      }

      result.push([{ separator: true, title: "" }]);
    } else {
      console.log('v1');
      createdAt = smartControlData?.dateResult;

      if (smartControlData.receivability === true) {
        result.push([
          i18next.t("viewer.smartCheckDetails.resultLabel"),
          i18next.t("viewer.smartCheckDetails.receivabilityControlledMsg")
        ]);
      } else {
        result.push([
          i18next.t("viewer.smartCheckDetails.resultLabel"),
          smartControlData.receivabilityResult?.final_error_message ||
          i18next.t("viewer.smartCheckDetails.receivabilityFalseMsg")
        ]);
      }

      if (smartControlData.receivabilityResult) {
        result.push([{ separator: true, title: i18next.t("viewer.smartCheckDetails.detailsTitle") }]);

        const { keywords, company_informations, expiration_date } = smartControlData.receivabilityResult;

        if (keywords) {
          result.push([
            i18next.t("viewer.smartCheckDetails.receivabilityLabel"),
            keywords.receivability
              ? i18next.t("viewer.smartCheckDetails.receivabilityTrueMsg")
              : keywords.final_error_message || i18next.t("viewer.smartCheckDetails.defaultErrorMsg")
          ]);
        }


        if (company_informations) {
          result.push([
            i18next.t("viewer.smartCheckDetails.companyInfoLabel"),
            company_informations.receivability
              ? i18next.t("viewer.smartCheckDetails.companyInfoValidMsg")
              : company_informations.final_error_message || i18next.t("viewer.smartCheckDetails.defaultErrorMsg")
          ]);

          pushCompanyInfo("sirenLabel", company_informations.details.siren, "sirenTooltipFound", "sirenTooltipNotFound");
          pushCompanyInfo("siretLabel", company_informations.details.siret, "siretTooltipFound", "siretTooltipNotFound");
          pushCompanyInfo("tvaLabel", company_informations.details.tva, "tvaTooltipFound", "tvaTooltipNotFound");
          pushCompanyInfo("companyNameLabel", company_informations.details.raison_sociale, "companyNameTooltipFound", "companyNameTooltipNotFound");
        }

        if (expiration_date) {
          result.push([
            i18next.t("viewer.smartCheckDetails.dateCheckLabel"),
            expiration_date.receivability
              ? i18next.t("viewer.smartCheckDetails.dateCheckValidMsg")
              : expiration_date.final_error_message || i18next.t("viewer.smartCheckDetails.defaultErrorMsg")
          ]);
        }
      }

      const hasValidSmartControlData =
        (smartControlData.informations?.length > 0 || smartControlData.dates?.length > 0);
      if (hasValidSmartControlData) {
        result.push([{ separator: true, title: i18next.t("viewer.smartCheckDetails.smartControlExtractionsTitle") }]);

        smartControlData.informations?.forEach(info => {
          const displayName = info.label?.trim() ? info.label : info.name;
          const value = typeof info.value === "boolean"
            ? (info.value ? i18next.t("viewer.smartCheckDetails.booleanTrue") : i18next.t("viewer.smartCheckDetails.booleanFalse"))
            : info.value || i18next.t("viewer.smartCheckDetails.noValueFound");
          result.push([displayName, value]);
        });

        smartControlData.dates?.forEach(date => {
          const displayName = date.label?.trim() ? date.label : date.name;
          result.push([displayName, date.formatted || i18next.t("viewer.smartCheckDetails.noValueFound")]);
        });
      }

      result.push([{ separator: true, title: "" }]);
    }
  } catch (error) {
    console.error("Erreur lors de la récupération des résultats smartControl:", error);
  }

  return { body: result, createdAt };
};

const Reason = ({ label }) => {
  const { classes } = useStyles();
  return (
    <li className={classes.reasonListItem}>
      <span>
        {label}
      </span>
    </li>
  )
}


const hasControl = (checks, control) => checks?.[control];
const mapSisIdReasons = (reasons) => reasons && Object.values(reasons);
const mapTrustpairReasons = (reasons) => reasons && Object.values(reasons)?.map(x => x.state_reasons)?.flat(1);

// Récupère la traduction en fonction du label
const getTrustpairReasonsTraduction = (label) => {
  switch (label) {
    case "Le code BIC est invalide":
      return i18next.t("trustPair.reason.invalidBic");
    case "Le compte bancaire est clos":
      return i18next.t("trustPair.reason.closedAccount");
    case "Le compte bancaire est clos depuis 14 mois ou plus":
      return i18next.t("trustPair.reason.closedAccountOver14Months");
    case "Le compte bancaire est clos depuis moins de 14 mois":
      return i18next.t("trustPair.reason.closedAccountUnder14Months");
    case "Le compte bancaire est valide":
      return i18next.t("trustPair.reason.validAccount");
    case "Le format du compte bancaire est invalide":
      return i18next.t("trustPair.reason.invalidAccountFormat");
    case "Le format du compte bancaire est invalide; Le format du code BIC est invalide":
      return i18next.t("trustPair.reason.invalidAccountAndBicFormat");
    case "Le numéro de routage est invalide":
      return i18next.t("trustPair.reason.invalidRoutingNumber");
    case "L'IBAN n'existe pas ou n'a pas de BIC associé":
      return i18next.t("trustPair.reason.nonexistentIbanOrBic");
    case "L'IBAN n'existe pas ou n'a pas de BIC associé; Le code BIC est invalide":
      return i18next.t("trustPair.reason.nonexistentIbanOrInvalidBic");
    case "L'appartenance du compte bancaire au fournisseur est en cours d'évaluation":
      return i18next.t("trustPair.reason.ongoingBankAccountValidation");
    case "L'appartenance du compte bancaire au fournisseur n'a pas été confirmée":
      return i18next.t("trustPair.reason.unconfirmedBankAccountOwnership");
    case "Le compte bancaire appartient à la société":
      return i18next.t("trustPair.reason.accountBelongsToCompany");
    case "Le compte bancaire appartient au fournisseur":
      return i18next.t("trustPair.reason.accountBelongsToProvider");
    case "Le couple fournisseur / compte bancaire est invalide":
      return i18next.t("trustPair.reason.invalidProviderAccount");
    case "Le statut juridique du tiers ne correspond pas au statut juridique du titulaire du compte bancaire":
      return i18next.t("trustPair.reason.mismatchedLegalStatus");
    case "La société est en activité":
      return i18next.t("trustPair.reason.companyIsActive");
    case "La société n'est plus en activité":
      return i18next.t("trustPair.reason.companyIsNotActive");
    case "Le format de l'identifiant de société est invalide":
      return i18next.t("trustPair.reason.invalidCompanyIdFormat");
    case "Les informations légales de l'entreprise n'ont pas pu être confirmées auprès des différentes sources":
      return i18next.t("trustPair.reason.unconfirmedLegalInformation");
    case "L'établissement est fermé":
      return i18next.t("trustPair.reason.closedBranchOffice");
    case "L’identifiant de la société n’a pas été renseigné":
      return i18next.t("trustPair.reason.companyIdNotDeclared");
    case "mais la société est toujours en activité":
      return i18next.t("trustPair.reason.companyStillActive");
    default:
      return label;  // Si aucun libellé ne correspond, retourne le libellé original
  }
}

// Récupère la traduction en fonction de la clé
const getSisIdReasonsTranlation = (key, label) => {
  const translationPath = `sisID.reason.${key}`;
  const keyExists = i18next.exists(translationPath);
  const translation = i18next.t(translationPath);
  const reasonWithGeneriqueCode = "Erreur"
  const reasonWithVariable = [
    "sisID.reason.reason.code.MV-M",
    "sisID.reason.reason.code.MV-U",
    "sisID.reason.reason.code.MV-NM"
  ]

  if (keyExists && !reasonWithVariable.includes(translationPath)) {
    return translation;
    // Code trop générique
  } else if (key.includes(reasonWithGeneriqueCode)) {
    switch (label) {
      case "Pays non couvert par Sis ID" :
        return i18next.t(`sisID.reason.ErreurCountry`);
      case "Erreur technique" :
        return i18next.t(`sisID.reason.ErreurTechnique`);
      case "Identifiant local non supporté par Sis ID" :
        return i18next.t(`sisID.reason.ErreurNotSupported`);
      default :
        return label;
    }
    // Le label nécessite qu'on injecte une variable
  } else if (reasonWithVariable.includes(translationPath) || key.includes("code.09")) {
    // On récupère le score dans le code d'erreur
    if (key.includes("code.09")) {
      const score = key.slice(-3);
      return i18next.t("sisID.reason.code.09", { score: score })
      // On récupère le nom du service situé entre : et . dans le label
    } else {
      const match = label.match(/:(.*$)/);
      const service = match ? match[1].trim() : ''
      return i18next.t(translationPath, { service: service })
    }
  } else {
    return label; // Si aucun code ne correspond retourne la chaine
  }
}

function PanelAdditionalChecks({ checks, smartChecks, v2smartCheck, extractor }) {
  const [cardTitle, setCardTitle] = useState();
  const [bodies, setBodies] = useState([]);


  useEffect(() => {
    const title = i18next.t("viewer.additionalChecks");
    const newBodies = [];

    const checkFailed = checks.smart_check_fail === "true";

    if (smartChecks?.informations || checkFailed) {
      const { body: smartCheckBody, createdAt } = buildSmartChecksBody(smartChecks, checkFailed, v2smartCheck);
      if (smartCheckBody?.length > 0) {
        newBodies.push({
          id: "SMART_CHECKS",
          header: i18next.t("viewer.smartCheck"),
          body: smartCheckBody,
          label: i18next.t("viewer.smartCheckLabelDate"),
          date: createdAt,
        });
      }
    }
    if (hasControl(checks, SubscriptionsControls.SIS_ID)) {
      newBodies.push({
        id: SubscriptionsControls.SIS_ID,
        header: i18next.t("viewer.paymentVerification"),
        body: buildSisIdBody(checks[SubscriptionsControls.SIS_ID]),
        label: i18next.t(SubscriptionsControls.SIS_ID),
        date: checks[SubscriptionsControls.SIS_ID].lastModifiedDate,
      });
    }
    if (hasControl(checks, SubscriptionsControls.TRUSTPAIR)) {
      newBodies.push({
        id: SubscriptionsControls.TRUSTPAIR,
        header: i18next.t("viewer.paymentVerification"),
        body: buildTrustpairBody(checks[SubscriptionsControls.TRUSTPAIR]),
        label: i18next.t(SubscriptionsControls.TRUSTPAIR),
        date: checks[SubscriptionsControls.TRUSTPAIR].updated_at,
      });
    }
    if (hasControl(checks, SubscriptionsControls.INDUED)) {
      newBodies.push({
        id: SubscriptionsControls.INDUED,
        header: i18next.t("indued.dueDiligence"),
        body: buildInduedBody(checks[SubscriptionsControls.INDUED]),
        label: i18next.t("indued.label"),
        date: checks[SubscriptionsControls.INDUED].due_diligence?.report_time,
      });
    }
    if (hasControl(checks, SubscriptionsControls.ECOVADIS)) {
      const evData = checks[SubscriptionsControls.ECOVADIS];
      newBodies.push({
        id: SubscriptionsControls.ECOVADIS,
        header: i18next.t("ecovadis.label"),
        body: buildEcovadisBody(checks[SubscriptionsControls.ECOVADIS]),
        label: i18next.t("ecovadis.label"),
        date: getEvDataScopeChangeFieldValue(evData, "published_date"),
      });
    }

    setBodies(newBodies);
    setCardTitle([title]);
  }, [checks, smartChecks, v2smartCheck]);

  return (
    <>
      {bodies.length > 0 || extractor ? (
        <>
          {bodies.map(({ id, header, body, label, date }, index) => (
            <DocViewerCard
              key={id}
              title={index === 0 ? cardTitle : null}
              marginBottom={!extractor && index === bodies.length - 1}
              body={
                <DocViewerTable
                  header={header}
                  body={body}
                  label={label}
                  date={date}
                />
              }
            />
          ))}
          {extractor && (
            <DocViewerCard
              key="EXTRACTOR"
              title={bodies?.length > 0 ? null : cardTitle}
              body={extractor}
              noMargin={bodies?.length > 0}
            />
          )}
        </>
      ) : (
        <DocViewerCard
          title={cardTitle}
          body={<DocViewerTable/>}
        />
      )}
    </>
  );
}
export default PanelAdditionalChecks;
